import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Auction, AuctionLot } from '../types/auction.types';
import { useAuth } from '../contexts/AuthContext';
import ImageUpload from '../components/shared/ImageUpload';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const EditAuction: React.FC = () => {
  const { auctionId } = useParams<{ auctionId: string }>();
  const { userProfile, isAuctioneer, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [auction, setAuction] = useState<Auction | null>(null);
  
  // Form fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [location, setLocation] = useState('');
  const [editingLot, setEditingLot] = useState<AuctionLot | null>(null);

  useEffect(() => {
    if (!isAuctioneer && !isAdmin) {
      toast.error('Access denied: Auctioneers only');
      navigate('/');
      return;
    }
    loadAuction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auctionId]);

  const loadAuction = async () => {
    if (!auctionId) return;

    try {
      const auctionDoc = await getDoc(doc(db, 'auctions', auctionId));
      
      if (auctionDoc.exists()) {
        const data = auctionDoc.data();
        const auctionData = {
          id: auctionDoc.id,
          ...data,
          startDate: data.startDate?.toDate() || new Date(),
          endDate: data.endDate?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Auction;

        // Check if user owns this auction
        if (!isAdmin && auctionData.createdBy !== userProfile?.uid) {
          toast.error('You do not have permission to edit this auction');
          navigate('/auctioneer/dashboard');
          return;
        }

        setAuction(auctionData);
        setTitle(auctionData.title);
        setDescription(auctionData.description || '');
        setStartDate(format(new Date(auctionData.startDate), "yyyy-MM-dd'T'HH:mm"));
        setEndDate(format(new Date(auctionData.endDate), "yyyy-MM-dd'T'HH:mm"));
        setLocation(auctionData.location || '');
      } else {
        toast.error('Auction not found');
        navigate('/auctioneer/dashboard');
      }
    } catch (error) {
      console.error('Error loading auction:', error);
      toast.error('Failed to load auction');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAuction = async () => {
    if (!auctionId || !auction) return;

    setSaving(true);
    try {
      const updateData = {
        title,
        description: description || null,
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        location: location || null,
        lots: auction.lots,
        totalLots: auction.lots.length,
        updatedAt: new Date()
      };

      await updateDoc(doc(db, 'auctions', auctionId), updateData);
      
      toast.success('Auction updated successfully');
      navigate('/auctioneer/dashboard');
    } catch (error) {
      console.error('Error updating auction:', error);
      toast.error('Failed to update auction');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAuction = async () => {
    if (!auctionId) return;

    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'auctions', auctionId));
      toast.success('Auction deleted successfully');
      navigate('/auctioneer/dashboard');
    } catch (error) {
      console.error('Error deleting auction:', error);
      toast.error('Failed to delete auction');
      setDeleting(false);
    }
  };

  const handleLotImagesUpdated = (lotId: string, urls: string[]) => {
    if (!auction) return;

    const updatedLots = auction.lots.map(lot => 
      lot.id === lotId ? { ...lot, images: urls } : lot
    );

    setAuction({ ...auction, lots: updatedLots });
  };

  const handleEditLot = (lot: AuctionLot) => {
    setEditingLot(lot);
  };

  const handleSaveLot = () => {
    if (!auction || !editingLot) return;

    const updatedLots = auction.lots.map(lot =>
      lot.id === editingLot.id ? editingLot : lot
    );

    setAuction({ ...auction, lots: updatedLots });
    setEditingLot(null);
    toast.success('Lot updated');
  };

  const handleClearAllImages = () => {
    if (!auction) return;

    const updatedLots = auction.lots.map(lot => ({
      ...lot,
      images: []
    }));

    setAuction({ ...auction, lots: updatedLots });
    toast.success('All images cleared from lots');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading auction...</div>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="card text-center py-12">
        <p className="text-xl text-gray-600">Auction not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Edit Auction</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="btn-secondary bg-red-50 text-red-600 hover:bg-red-100"
          >
            🗑️ Delete Auction
          </button>
          <button
            onClick={() => navigate('/auctioneer/dashboard')}
            className="btn-secondary"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Auction Details */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">Auction Details</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Auction Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              rows={3}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date & Time
              </label>
              <input
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date & Time
              </label>
              <input
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Bulk Upload Button */}
          <div className="pt-4 border-t">
            <div className="flex gap-3">
              <Link
                to={`/auctioneer/bulk-upload/${auctionId}`}
                className="btn-secondary"
              >
                📁 Bulk Upload Images
              </Link>
              <button
                onClick={handleClearAllImages}
                className="btn-secondary bg-orange-50 text-orange-600 hover:bg-orange-100"
              >
                🗑️ Clear All Images
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Upload all images at once using the SKU_01.jpg naming format, or clear all existing images
            </p>
          </div>
        </div>
      </div>

      {/* Lots List */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">Auction Lots ({auction.lots.length})</h2>
        
        <div className="space-y-4">
          {auction.lots.map((lot) => (
            <div key={lot.id} className="border rounded-lg p-4">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-sm text-gray-600">SKU: {lot.sku}</p>
                  <h3 className="font-bold text-lg">{lot.title}</h3>
                  <p className="text-sm text-gray-600">{lot.description}</p>
                  
                  {/* Expert Estimates Display */}
                  {lot.expertLowEstimate && lot.expertHighEstimate && (
                    <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded text-sm">
                      <span>🎯 Expert Range:</span>
                      <span className="font-semibold">${lot.expertLowEstimate} - ${lot.expertHighEstimate}</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleEditLot(lot)}
                  className="btn-secondary text-sm"
                >
                  Edit Details
                </button>
              </div>

              <ImageUpload
                sku={lot.sku}
                existingImages={lot.images}
                onImagesUploaded={(urls: string[]) => handleLotImagesUpdated(lot.id, urls)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end space-x-4">
        <button
          onClick={() => navigate('/auctioneer/dashboard')}
          className="btn-secondary"
          disabled={saving}
        >
          Cancel
        </button>
        <button
          onClick={handleSaveAuction}
          disabled={saving}
          className="btn-primary disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Edit Lot Modal */}
      {editingLot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Edit Lot Details</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={editingLot.title}
                  onChange={(e) => setEditingLot({ ...editingLot, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={editingLot.description}
                  onChange={(e) => setEditingLot({ ...editingLot, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Starting Bid
                  </label>
                  <input
                    type="number"
                    value={editingLot.startingBid}
                    onChange={(e) => setEditingLot({ ...editingLot, startingBid: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    step="0.01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <input
                    type="text"
                    value={editingLot.category}
                    onChange={(e) => setEditingLot({ ...editingLot, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              {/* Expert Estimates */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3 flex items-center">
                  <span className="mr-2">🎯</span>
                  Expert Price Estimates (Optional but Recommended)
                </h4>
                <p className="text-sm text-gray-600 mb-3">
                  Set your expert price range. Users who predict outside this range and are correct earn bonus multipliers!
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Low Estimate ($)
                    </label>
                    <input
                      type="number"
                      value={editingLot.expertLowEstimate || ''}
                      onChange={(e) => setEditingLot({ 
                        ...editingLot, 
                        expertLowEstimate: e.target.value ? parseFloat(e.target.value) : undefined 
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="e.g. 80"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      High Estimate ($)
                    </label>
                    <input
                      type="number"
                      value={editingLot.expertHighEstimate || ''}
                      onChange={(e) => setEditingLot({ 
                        ...editingLot, 
                        expertHighEstimate: e.target.value ? parseFloat(e.target.value) : undefined 
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="e.g. 120"
                      step="0.01"
                    />
                  </div>
                </div>

                {editingLot.expertLowEstimate && editingLot.expertHighEstimate && (
                  <div className="mt-2 p-3 bg-blue-50 rounded text-sm">
                    <p className="text-blue-800">
                      <strong>Range:</strong> ${editingLot.expertLowEstimate} - ${editingLot.expertHighEstimate}
                    </p>
                    <p className="text-blue-700">
                      <strong>Spread:</strong> ${editingLot.expertHighEstimate - editingLot.expertLowEstimate} 
                      ({(((editingLot.expertHighEstimate - editingLot.expertLowEstimate) / editingLot.expertLowEstimate) * 100).toFixed(0)}%)
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      💡 Tip: Tighter ranges (10-20% spread) increase difficulty bonuses for predictors
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setEditingLot(null)}
                className="flex-1 btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLot}
                className="flex-1 btn-primary"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4 text-red-600">⚠️ Delete Auction?</h3>
            
            <p className="text-gray-700 mb-4">
              Are you sure you want to delete this auction? This action cannot be undone.
            </p>

            <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
              <p className="text-sm text-red-700">
                <strong>Warning:</strong> Deleting this auction will:
              </p>
              <ul className="text-sm text-red-600 mt-2 ml-4 list-disc">
                <li>Permanently remove all {auction.totalLots} lots</li>
                <li>Delete all associated images</li>
                <li>Remove any predictions made by users</li>
              </ul>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 btn-secondary"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAuction}
                disabled={deleting}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete Auction'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditAuction;